/**
 * Client-side GTM ecommerce helpers.
 *
 * Fetches enriched GA4 payloads from BFF routes and builds items from
 * in-memory product data when available (PLP/PDP).
 */

import type { ProductCardData } from '@/components/brand';
import { BRAND_NAME } from '@/lib/const';
import type { Product, ProductType } from '@/lib/product/types';
import type { CheckoutLine } from '@/states/checkout/checkoutState';
import type { EcommercePayload, Item } from './types';
import type { GtmLineInput } from './ecommerceSchemas';

/** Checkout line fields used for GTM BFF enrichment — accepts Saleor lines and Valtio snapshots. */
export type GtmCheckoutLineSource = {
  readonly id: string;
  readonly quantity: number;
  readonly unitPrice: {
    readonly gross: { readonly amount: number; readonly currency: string };
    readonly net: { readonly amount: number; readonly currency: string };
  };
  readonly undiscountedUnitPrice?: { readonly amount: number; readonly currency: string } | null;
  readonly variant?: {
    readonly sku?: string | null;
    readonly product?: {
      readonly name?: string;
      readonly slug?: string;
      readonly productType?: { readonly slug?: string } | null;
      readonly caseMaterial?: {
        readonly values?: ReadonlyArray<{
          readonly translation?: { readonly name?: string | null } | null;
        }> | null;
      } | null;
      readonly color?: {
        readonly values?: ReadonlyArray<{ readonly slug?: string | null }> | null;
      } | null;
      readonly strapColor?: {
        readonly values?: ReadonlyArray<{
          readonly plainText?: string | null;
          readonly translation?: {
            readonly name?: string | null;
            readonly plainText?: string | null;
          } | null;
        }> | null;
      } | null;
      readonly size?: {
        readonly values?: ReadonlyArray<{ readonly slug?: string | null }> | null;
      } | null;
    } | null;
  } | null;
};

function normalizeItemId(sku: string): string {
  return sku.replaceAll('.', '_');
}

function mapProductType(productType?: ProductCardData['productType'] | ProductType): string {
  if (!productType) return 'watch';
  return productType.toLowerCase();
}

function resolveProductCardPrice(product: ProductCardData): number {
  if (typeof product.priceAmount === 'number') {
    return product.priceAmount;
  }
  if (typeof product.price === 'number') {
    return product.price;
  }
  return 0;
}

export function buildItemFromProductCard(
  product: ProductCardData,
  index = 0,
  itemListName?: string
): Item {
  const price = resolveProductCardPrice(product);

  return {
    index,
    item_id: normalizeItemId(product.sku),
    item_name: product.name.toLowerCase(),
    item_brand: BRAND_NAME.toLowerCase(),
    item_category: mapProductType(product.productType),
    item_sub_category: 'regular',
    item_list_name: itemListName,
    item_case_material: product.material?.toUpperCase(),
    item_dial_color: product.dialColor?.toLowerCase(),
    price,
    quantity: 1,
    currency: product.currency ?? 'USD',
    item_wishlist_compared: 'no',
    discount: 0,
  };
}

export function buildItemFromProduct(product: Product, index = 0): Item {
  const variantLabel = product.variants?.find((v) => v.sku === product.sku)?.name;

  return {
    index,
    item_id: normalizeItemId(product.sku),
    item_name: product.name.toLowerCase(),
    item_brand: BRAND_NAME.toLowerCase(),
    item_category: mapProductType(product.productType),
    item_sub_category: 'regular',
    item_collection: product.collectionSlug,
    item_case_material: product.caseMaterial?.toUpperCase(),
    item_dial_color: product.dialColor?.toLowerCase(),
    item_variant: variantLabel?.toLowerCase(),
    price: product.price.amount,
    quantity: 1,
    currency: product.price.currency,
    item_wishlist_compared: 'no',
    discount: 0,
  };
}

export function buildEcommerceFromProductCards(
  products: ProductCardData[],
  itemListName?: string
): EcommercePayload {
  if (products.length === 0) {
    throw new Error('[GTM] buildEcommerceFromProductCards called with no products');
  }

  const items = products.map((product, index) =>
    buildItemFromProductCard(product, index, itemListName)
  );
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return {
    value: +value.toFixed(2),
    currency: items[0]?.currency,
    items,
  };
}

export function checkoutOptimisticLineToGtmInput(line: CheckoutLine): GtmLineInput {
  return {
    id: line.id,
    quantity: line.quantity,
    sku: line.sku,
    name: line.name,
    productType: line.productType,
    price: line.price,
    currency: line.currency,
  };
}

export function resolveGtmLines(
  checkoutLines: ReadonlyArray<GtmCheckoutLineSource> | undefined,
  optimisticLines: ReadonlyArray<CheckoutLine>
): GtmLineInput[] {
  const lines = checkoutLines?.length
    ? checkoutLines.map(checkoutLineToGtmInput)
    : optimisticLines.map(checkoutOptimisticLineToGtmInput);

  return lines.filter((line) => line.quantity > 0);
}

export function checkoutLineToGtmInput(line: GtmCheckoutLineSource): GtmLineInput {
  const product = line.variant?.product;

  return {
    id: line.id,
    quantity: line.quantity,
    sku: line.variant?.sku ?? undefined,
    name: product?.name,
    productType: product?.productType?.slug,
    unitPrice: {
      gross: {
        amount: line.unitPrice.gross.amount,
        currency: line.unitPrice.gross.currency,
      },
      net: {
        amount: line.unitPrice.net.amount,
        currency: line.unitPrice.net.currency,
      },
    },
    undiscountedUnitPrice: line.undiscountedUnitPrice
      ? {
          amount: line.undiscountedUnitPrice.amount,
          currency: line.undiscountedUnitPrice.currency,
        }
      : undefined,
    variant: line.variant
      ? {
          sku: line.variant.sku,
          product: product
            ? {
                name: product.name ?? '',
                slug: product.slug ?? '',
                productType: product.productType?.slug ? { slug: product.productType.slug } : null,
                caseMaterial: product.caseMaterial ?? null,
                color: product.color ?? null,
                strapColor: product.strapColor ?? null,
                size: product.size ?? null,
              }
            : null,
        }
      : null,
  } as GtmLineInput;
}

interface EcommerceApiResponse {
  success: boolean;
  data?: EcommercePayload;
  errors?: Array<{ message: string }>;
}

export async function fetchEcommerceBySkus({
  locale,
  skus,
  itemListName,
  coupon,
}: {
  locale: string;
  skus: string[];
  itemListName?: string;
  coupon?: string;
}): Promise<EcommercePayload> {
  const response = await fetch('/api/gtm/ecommerce/by-skus', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locale, skus, itemListName, coupon }),
  });

  const json = (await response.json()) as EcommerceApiResponse;
  if (!response.ok || !json.success || !json.data) {
    throw new Error(json.errors?.[0]?.message ?? 'Failed to enrich ecommerce by SKUs');
  }

  return json.data;
}

export async function fetchEcommerceByLines({
  locale,
  lines,
  coupon,
  totalValue,
  includeCurrency = true,
}: {
  locale: string;
  lines: GtmLineInput[];
  coupon?: string;
  totalValue?: number;
  includeCurrency?: boolean;
}): Promise<EcommercePayload> {
  const response = await fetch('/api/gtm/ecommerce/by-lines', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locale, lines, coupon, totalValue, includeCurrency }),
  });

  const json = (await response.json()) as EcommerceApiResponse;
  if (!response.ok || !json.success || !json.data) {
    throw new Error(json.errors?.[0]?.message ?? 'Failed to enrich ecommerce by lines');
  }

  return json.data;
}

/** Legacy GA4 dataLayer contract — item_brand is omitted from pushed items. */
export function stripItemBrandFromEcommercePayload(ecommerce: EcommercePayload): EcommercePayload {
  if (!ecommerce.items?.length) {
    return ecommerce;
  }

  return {
    ...ecommerce,
    items: ecommerce.items.map(({ item_brand: _unused, ...item }) => item),
  };
}

/** Discovery events (view_item, view_item_list, select_item) omit top-level value/currency. */
export function omitEcommerceValueAndCurrency(ecommerce: EcommercePayload): EcommercePayload {
  const { value: _value, currency: _currency, ...rest } = ecommerce;
  return rest;
}
